import React, { useEffect, useState } from "react";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { roleSchema } from "../../utils/validationSchema/roleValidationSchema";

const useRolePermission = ({ id }) => {
  const { authData } = useSelector((state) => state.loginSlice);
  const [roleData, setRoleData] = useState({
    systemPermissions: [],
  });
  const dispatch = useDispatch();

  const [changeDetails, setChangeDetails] = useState({
    sName: "",
    sDescription: "",
  });
  const navigate = useNavigate();
  // const [selectedPermissions, setSelectedPermissions] = useState(
  //   new Set(roleData.roleDetails.permissionIds)
  // );

  const [addedPermissions, setAddedPermissions] = useState([]);
  const [removedPermissions, setRemovedPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);

  const [submitLoader, setSubmitLoader] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState();
  const filteredAddedPermissions = addedPermissions.filter(
    (id) => !initialPermissions.includes(id) // Keep only newly added IDs
  );

  const filteredRemovedPermissions = removedPermissions.filter(
    (id) => initialPermissions.includes(id) // Keep only IDs that were originally selected
  );

  const [expandedApps, setExpandedApps] = useState({});
  const [expandedModules, setExpandedModules] = useState({});

  const toggleAppExpand = (appId, modules) => {
    setExpandedApps((prev) => {
      const isClosing = prev[appId];
      if (isClosing) {
        // Collapse all modules inside this app
        const updatedModules = { ...expandedModules };
        modules?.forEach((module) => delete updatedModules[module.moduleId]);
        setExpandedModules(updatedModules);
      }
      return { ...prev, [appId]: !prev[appId] };
    });
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const updatePermissions = (permissions, isChecked) => {
    setSelectedPermissions((prevPermissions) => {
      if (isChecked) {
        // Add new permissions while avoiding duplicates
        const newPermissions = [
          ...new Set([...prevPermissions, ...permissions]),
        ];

        setAddedPermissions((prev) => [...prev, ...permissions]);
        setRemovedPermissions((prev) =>
          prev.filter((id) => !permissions.includes(id))
        );

        return newPermissions;
      } else {
        // Remove the permissions
        const updatedPermissions = prevPermissions.filter(
          (id) => !permissions.includes(id)
        );

        setRemovedPermissions((prev) => [...prev, ...permissions]);
        setAddedPermissions((prev) =>
          prev.filter((id) => !permissions.includes(id))
        );

        return updatedPermissions;
      }
    });
  };

  const handlePermissionChange = (permissionId) => {
    updatePermissions(
      [permissionId],
      !selectedPermissions.includes(permissionId)
    );
  };

  const handleModuleChange = (module) => {
    const permissionIds = module.modulePermissions.map(
      (perm) => perm.permissionId
    );
    const isChecked = permissionIds.every((id) =>
      selectedPermissions.includes(id)
    );
    updatePermissions(permissionIds, !isChecked);
  };

  const handleApplicationChange = (application) => {
    const allPermissionIds = application.modules.flatMap((mod) =>
      mod.modulePermissions.map((perm) => perm.permissionId)
    );
    const isChecked = allPermissionIds.every((id) =>
      selectedPermissions.includes(id)
    );
    updatePermissions(allPermissionIds, !isChecked);
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    setChangeDetails((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const getRoleById = async () => {
    try {
      setLoader(true);
      const response = await apiHandler({
        url: `${endpoint.GET_ROLE_BY_ID}/${id}`,
        method: "GET",
        authToken: authData,
        dispatch,
      });
      if (response.data.success) {
        setRoleData(response.data.data);
        setChangeDetails({
          sName: response.data.data?.roleDetails?.name
            ? response.data.data.roleDetails?.name
            : "",
          sDescription: response.data.data.roleDetails?.description
            ? response.data.data.roleDetails.description
            : "",
        });
        // let appExpands = response.data.data.systemPermissions?.reduce(
        //   (acc, app) => {
        //     acc[app.applicationId] = true; // Expand all applications by default
        //     return acc;
        //   },
        //   {}
        // );

        // let moduleExpands = response.data.data.systemPermissions?.reduce(
        //   (acc, app) => {
        //     app.modules?.forEach((module) => {
        //       acc[module.moduleId] = true; // Expand all modules by default
        //     });
        //     return acc;
        //   },
        //   {}
        // );
        // setExpandedModules(moduleExpands);
        // setExpandedApps(appExpands);

        setSelectedPermissions(
          response.data.data.roleDetails.permissionIds
            ? response.data.data.roleDetails.permissionIds
            : []
        );
        setInitialPermissions(
          response.data.data.roleDetails.permissionIds
            ? response.data.data.roleDetails.permissionIds
            : []
        );
      }
    } catch (error) {

    } finally {
      setLoader(false);
    }
  };

  function getMessageForValidationError(errors, stringToMatch) {
    for (const error of errors) {
      if (error.path && error.path.length > 1) {
        var acc = {};

        if (error.path[0] === "NomineeList") {
          acc[`${error.path[2]}${error.path[1]}`] = error.message;
        }
        return acc;
      } else if (
        error.path &&
        error.path.length > 0 &&
        error.path[0] === stringToMatch
      ) {
        return error.message;
      }
    }
    return null;
  }

  const handleBlur = (e) => {
    const { name, value } = e.target;

    let validationbody = {
      [name]: value,
    };

    const validationResult = roleSchema.validate(validationbody, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validationResult.error) {
      const error = getMessageForValidationError(
        validationResult.error.details,
        name
      );

      setErrors({ ...errors, [name]: error });
    } else {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = () => {
    const data = {
      sName: changeDetails.sName,
      sDescription: changeDetails.sDescription,
    };
    const validationResult = roleSchema.validate(data, {
      abortEarly: false,
    });

    if (validationResult.error) {
      let errors = null;
      errors = validationResult.error.details.reduce((acc, error) => {
        acc[error.path[0]] = error.message;
        return acc;
      }, {});

      setErrors(errors);
    } else {
      if (id) {
        updateRole();
      } else {

        createRole();
      }
    }
  };

  const updateRole = async () => {
    try {
      setSubmitLoader(true);
      let data = {
        id: id,
        name: changeDetails.sName,
        description: changeDetails.sDescription,
        permissionsJson: {
          addedPermissions: filteredAddedPermissions,
          removedPermissions: filteredRemovedPermissions,
        },
      };
      const response = await apiHandler({
        url: endpoint.UPDATE_ROLE,
        method: "PATCH",
        authToken: authData,
        data: data,
        dispatch,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        navigate("/role");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
   
    } finally {
      setSubmitLoader(false);
    }
  };

  const createRole = async () => {
    try {
      setSubmitLoader(true);
      let data = {
        name: changeDetails.sName,
        description: changeDetails.sDescription,
        permissions: addedPermissions,
      };
      const response = await apiHandler({
        url: endpoint.CREATE_ROLE,
        method: "POST",
        authToken: authData,
        data: data,
        dispatch,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        navigate("/role");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
       } finally {
      setSubmitLoader(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      setLoader(true);
      const response = await apiHandler({
        url: `${endpoint.GET_PERMISSIONS}`,
        method: "GET",
        authToken: authData,
        dispatch,
      });
      if (response.data.success) {
        let appExpands = response.data.data?.reduce((acc, app) => {
          acc[app.applicationId] = true; // Expand all applications by default
          return acc;
        }, {});

        let moduleExpands = response.data.data?.reduce((acc, app) => {
          app.modules?.forEach((module) => {
            acc[module.moduleId] = true; // Expand all modules by default
          });
          return acc;
        }, {});
        setExpandedModules(moduleExpands);
        setExpandedApps(appExpands);
        setRoleData((prev) => {
          return {
            ...prev,
            systemPermissions: response.data.data,
          };
        });
      }
    } catch (error) {
    
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!id) {
      fetchPermissions();
    }
  }, []);

  useEffect(() => {
    if (id) {
      getRoleById();
    }
  }, []);

  const renderErrorMessage = (fieldName) => {
    if (errors && errors[fieldName]) {
      return errors[fieldName];
    }
    return null;
  };

  return {
    roleData,
    addedPermissions,
    removedPermissions,
    updatePermissions,
    selectedPermissions,
    handleModuleChange,
    handleApplicationChange,
    handlePermissionChange,
    handleChange,
    changeDetails,
    handleSubmit,
    submitLoader,
    loader,
    errors,
    renderErrorMessage,
    handleBlur,
    expandedApps,
    expandedModules,
    toggleAppExpand,
    toggleModuleExpand,
  };
};

export default useRolePermission;
